//external
import React from "react";
import { transform } from "ol/proj";

//components
import OLLayerMap from "./OLLayerMap";
import * as olStyles from "./olStyles";
import { reverseGeocode } from "../../utils/reverseGeocode";

//actions and calls
import api from "../../store/api/api";
import FeedbackTile from "../theme/modals/FeedbackTile";

const ADDRESS_SEARCH_MIN_CHARS = 3;
const ADDRESS_SEARCH_DEBOUNCE_MS = 500;

// Shared legacy/uber dropoff-assignment UI: map + per-seat canvasser cards, each
// with its own dropoff address autocomplete. The actual address suggestion source
// (Google Places vs Uber) is injected via the `searchAddresses` prop -- see
// TurfDropoffMapModalLegacy.js and TurfDropoffMapModalUber.js, which supply
// it and are picked between by TurfDropoffMapModal.js.
class TurfDropoffMapModalBase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      layers: [],
      canvassers: [],
      selectedSeatId: 0,
      // which of a seat's address fields ("dropoff" | "ending_pickup") a map
      // click currently targets
      selectedField: "dropoff",
      allAssigned: false,
      loading: true,
      // keyed by `${seat_id}:${field}`: { query, results, loading, error, open, activeIndex }
      addressSearch: {},
    };

    this.addressSearchRefs = {};
    this.addressSearchTimeouts = {};
  }

  componentDidMount() {
    let { crews, shift, crew: crew_id, canvassers: campaignCanvassers } = this.props;
    if (!shift) {
      console.error("No shift id was passed to the dropoff modal props.");
    }

    document.addEventListener("mousedown", this.handleClickOutsideAddressSearch);

    if (!crew_id) {
      const shiftCrew = crews.find((c) => c.shift === shift);
      crew_id = shiftCrew ? shiftCrew.id : 0;
    }

    const crew = crews.find((c) => c.id === crew_id);
    const seats = crew ? crew.seats : [];

    // seat.canvasser is already the correct, per-seat canvasser id straight off the
    // crew -- no need to round-trip through the (buggy, shift-scoped) assigned-turfs
    // report just to resolve a name. Names come from the campaign's own canvasser
    // list instead, keyed by id.
    const canvasserById = {};
    (campaignCanvassers || []).forEach((c) => {
      canvasserById[c.id] = c;
    });

    const turfIds = [...new Set(seats.map((s) => s.turf).filter(Boolean))];

    Promise.all(
      turfIds.map((turfId) =>
        api.get(`turfs/${turfId}/`).then((res) => [turfId, res.data])
      )
    ).then((turfEntries) => {
      const householdsByTurf = Object.fromEntries(turfEntries);

      const canvassers = seats.map((seat) => {
        const assigned = seat.canvasser != null ? canvasserById[seat.canvasser] : null;
        return {
          // keyed by seat (crewcanvasser) id rather than the canvasser's own
          // id -- unlike the canvasser, the seat always exists and is always
          // unique, so it's the identity every other method in this
          // component keys off of
          seat_id: seat.crewcanvasser_id,
          crewcanvasser_id: seat.crewcanvasser_id,
          crew_id: crew.id,
          firstname: assigned ? assigned.firstname : "Unassigned",
          lastname: assigned ? assigned.lastname : "",
          targethouseholds: householdsByTurf[seat.turf] || [],
          dropoff_lat: seat.dropoff_lat,
          dropoff_lon: seat.dropoff_lon,
          dropoff_address: seat.dropoff_address,
          ending_pickup_lat: seat.ending_pickup_lat,
          ending_pickup_lon: seat.ending_pickup_lon,
          ending_pickup_address: seat.ending_pickup_address,
          comments: seat.comments,
        };
      });

      // seed the address search boxes from any already-persisted address, so a
      // previously assigned seat doesn't come back with blank inputs on reload
      const addressSearch = {};
      canvassers.forEach((c) => {
        if (c.dropoff_address) {
          addressSearch[this.addressSearchKey(c.seat_id, "dropoff")] = {
            query: c.dropoff_address,
          };
        }
        if (c.ending_pickup_address) {
          addressSearch[this.addressSearchKey(c.seat_id, "ending_pickup")] = {
            query: c.ending_pickup_address,
          };
        }
      });

      let selectedSeatId = 0;
      if (canvassers.length)
        selectedSeatId = (
          canvassers.find((d) => !d.dropoff_lat) || canvassers[0]
        ).seat_id;
      this.setState(
        { canvassers, selectedSeatId, addressSearch },
        this.buildMapLayers
      );
    });
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutsideAddressSearch);
    Object.values(this.addressSearchTimeouts).forEach(
      (timeout) => timeout && clearTimeout(timeout)
    );
  }

  // addressSearch/addressSearchRefs are keyed by seat *and* field, since each seat
  // now has two independent address inputs (dropoff, ending_pickup)
  addressSearchKey = (seatId, field) => `${seatId}:${field}`;

  getColorFromQualitativeScheme = (idx) => {
    const qualitative = [
      "#33a02c",
      "#1f78b4",
      "#6a3d9a",
      "#a6cee3",
      "#fb9a99",
      "#e31a1c",
      "#fdbf6f",
      "#b2df8a",
      "#ff7f00",
      "#cab2d6",
      "#ffff99",
      "#b15928",
    ];

    return qualitative[idx % qualitative.length];
  };

  buildMapLayers = () => {
    let { canvassers } = this.state;
    let layers = [];

    canvassers.forEach((d) => {
      let fill = this.getColorFromQualitativeScheme(d.seat_id);
      let layer_id = `${d.seat_id}-points`;
      let features = d.targethouseholds.map((d) => ({
        ...d,
        layer_id,
        style: olStyles.genericSquareStyle({ fill }),
      }));

      // add points

      layers.push({
        features,
        featureType: "points", // can be 'points', 'convex_hull', 'line_string'
        id: layer_id,
      });

      //add convex hulls
      layer_id = `${d.seat_id}-convexhull`;
      layers.push({
        features,
        featureType: "convex_hull",
        groupBy: "turf_name",
        getGroupedFeatureStyle: () =>
          olStyles.coloredPolygonStyle(fill, 3, 0.8),
        id: layer_id,
      });

      //add dropoff point if present
      if (d.dropoff_lat) {
        layer_id = `${d.seat_id}-dropoff`;
        let newLayerFeatures = [
          {
            longitude: d.dropoff_lon,
            latitude: d.dropoff_lat,
            layer_id,
            style: olStyles.centroidStyle({ fill, radius: 8 }),
          },
        ];

        layers.push({
          features: newLayerFeatures,
          featureType: "points",
          id: layer_id,
        });
      }

      //add ending pickup point if present -- crossStyle so it's visually distinct
      //from the round dropoff marker
      if (d.ending_pickup_lat) {
        layer_id = `${d.seat_id}-ending_pickup`;
        let endingPickupFeatures = [
          {
            longitude: d.ending_pickup_lon,
            latitude: d.ending_pickup_lat,
            layer_id,
            style: olStyles.crossStyle({ fill, radius: 8 }),
          },
        ];

        layers.push({
          features: endingPickupFeatures,
          featureType: "points",
          id: layer_id,
        });
      }
    });

    this.setState({ layers, loading: false });
  };

  // assign (or clear, if lat/lon are null) a seat's coordinates for the given field
  // ("dropoff" | "ending_pickup"). Shared by map-click selection and
  // address-autocomplete selection. `address` is only known once a suggestion has
  // actually been picked (selectAddressResult) -- a bare map click assigns lat/lon
  // with no address until/unless the user then confirms a reverse-geocoded suggestion.
  assignLocation = (seatId, field, latitude, longitude, address = null) => {
    latitude = latitude != null && latitude !== "" ? +latitude : null;
    longitude = longitude != null && longitude !== "" ? +longitude : null;

    const latKey = `${field}_lat`;
    const lonKey = `${field}_lon`;
    const addressKey = `${field}_address`;

    let canvasser = this.state.canvassers.find(
      (d) => d.seat_id === seatId
    );
    if (!canvasser) return;

    let canvassers = this.state.canvassers.map((d) =>
      d.seat_id === seatId
        ? { ...d, [latKey]: latitude, [lonKey]: longitude, [addressKey]: address }
        : d
    );

    let newLayerId = `${seatId}-${field}`;

    let layers = this.state.layers.filter((d) => d.id !== newLayerId);
    let canvasserFill = this.getColorFromQualitativeScheme(seatId);
    let newLayerFeatures = [
      {
        longitude,
        latitude,
        layer_id: newLayerId,
        style:
          field === "dropoff"
            ? olStyles.genericCircleStyle({ fill: canvasserFill, radius: 8 })
            : olStyles.crossStyle({ fill: canvasserFill, radius: 8 }),
      },
    ];

    longitude &&
      layers.push({
        features: newLayerFeatures,
        featureType: "points",
        id: newLayerId,
      });

    /*
     * the coordinates need to be updated in three places:
     *
     *
     *  1) State, so that we draw the point on the map and handle any side effects
     *  2) The crewcanvasser table on the database, which is where the data persists
     *  3) The doorcrew redux reducer, so that side effects and displays can be handled on the scheduler page without reload
     *
     * */
    this.setState({ layers, canvassers }, () => {
      // auto-advance-to-next-unassigned only applies to dropoff -- ending_pickup
      // is a secondary field and shouldn't hijack seat selection while it's being set
      if (field !== "dropoff") return;

      let nextCanvasser = this.state.canvassers.find((d) => !d.dropoff_lat);
      if (nextCanvasser) {
        this.setState({ selectedSeatId: nextCanvasser.seat_id, selectedField: "dropoff" });
      } else {
        this.setState({ allAssigned: true });
      }
    });

    // update in database
    api
      .patch(`/doorcrewcanvassers/${canvasser.crewcanvasser_id}/`, {
        [latKey]: latitude,
        [lonKey]: longitude,
        [addressKey]: address,
      })
      .then((res) => {
        //api call returns updated crew canvasser. The last step in the process is too update this in doorcrewcanvasser reducer
        //We need to replace the seat and call update crew

        let crew = this.props.crews.find((d) => d.id === canvasser.crew_id);

        if (!crew) console.log("ERROR ERROR no crew found");
        let seatIndex = crew.seats.findIndex(
          (d) => d.id === canvasser.crewcanvasser_id
        );
        crew.seats[seatIndex][latKey] = res.data[latKey];
        crew.seats[seatIndex][lonKey] = res.data[lonKey];
        crew.seats[seatIndex][addressKey] = res.data[addressKey];

        this.props.updateCrew(crew);
      });
  };

  // assign the seat's active-field coordinates from a map click
  handleMapClick = (olEvt) => {
    let lonlat =
      olEvt.coordinate && transform(olEvt.coordinate, "EPSG:3857", "EPSG:4326");
    let longitude = lonlat ? lonlat[0].toFixed(6) : null;
    let latitude = lonlat ? lonlat[1].toFixed(6) : null;
    if (!latitude || !longitude) return;

    // captured once: this map click is fired from OL's native "singleclick" handler,
    // outside any React synthetic event, so under React 16's legacy batching
    // assignLocation's setState (and its auto-advance-to-next-canvasser callback)
    // applies synchronously -- re-reading this.state.selectedSeatId afterward
    // would already point at the *next* canvasser instead of the one just clicked
    const seatId = this.state.selectedSeatId;
    const field = this.state.selectedField;
    this.assignLocation(seatId, field, latitude, longitude);
    this.primeCanvasserAddressFromClick(seatId, field, latitude, longitude);
  };

  // shared by canvasser-card click/focus handlers: makes the given canvasser+field
  // (dropoff or ending_pickup) the map-click target
  focusCanvasser = (seatId, field = "dropoff") => {
    this.setState((prev) => {
      if (prev.selectedSeatId === seatId && prev.selectedField === field) {
        return null;
      }
      return { selectedSeatId: seatId, selectedField: field };
    });
  };

  handleCanvasserCardMouseOver = (canvasserData) => {
    let { seat_id } = canvasserData;
    let layers = [...this.state.layers];
    let convexHullLayer = this.state.layers.find(
      (d) => d.id === `${seat_id}-convexhull`
    );
    if (!convexHullLayer) return;
    let fill = this.getColorFromQualitativeScheme(seat_id);
    convexHullLayer = {
      ...convexHullLayer,
      getGroupedFeatureStyle: () => olStyles.coloredPolygonStyle(fill, 9, 1),
      id: "hover-convex-hull",
    };

    layers = [...layers, convexHullLayer];
    this.setState({ layers });
  };

  handleCanvasserCardMouseOut = () => {
    let layers = [
      ...this.state.layers.filter(
        (d) => d.id !== "hover-convex-hull" && d.id !== "hover-point"
      ),
    ];
    this.setState({ layers });
  };

  handlePointMouseOver = (pointData) => {
    let { layer_id, latitude, longitude } = pointData;
    let layers = [...this.state.layers];
    let pointLayer = this.state.layers.find((d) => d.id === layer_id);
    console.log(pointLayer, "pointLayer");
    if (!pointLayer) return;
    let fill = this.getColorFromQualitativeScheme(pointLayer.id.split("-")[0]);
    pointLayer = {
      ...pointLayer,
      features: [
        {
          ...pointLayer.features[0],
          style: olStyles.genericCircleStyle({ fill, radius: 10 }),
        },
      ],
      id: "hover-point",
    };
    layers = [...layers, pointLayer];
    console.log("layers", layers);
    this.setState({ layers });
  };

  handleFeatureHover = (marker) => {
    console.log(marker);
    if (marker && marker.id) {
      this.handleCanvasserCardMouseOver({
        seat_id: marker.id.split("-")[0],
      });
    } else if (marker && marker.layer_id) {
      this.handlePointMouseOver(marker);
    } else {
      this.handleCanvasserCardMouseOut();
    }
  };

  unassignLocation = (seatId, field) => {
    this.assignLocation(seatId, field, null, null);
    const key = this.addressSearchKey(seatId, field);
    this.setState((prev) => ({
      addressSearch: {
        ...prev.addressSearch,
        [key]: { query: "", results: [], loading: false, error: false, open: false, activeIndex: -1 },
      },
    }));
  };

  // --- address autocomplete (POC) ---
  // proximity-biases the search around the centroid of the canvasser's assigned turf;
  // the actual suggestion source is injected via this.props.searchAddresses
  getCanvasserSearchCenter = (canvasser) => {
    const points = (canvasser.targethouseholds || []).filter(
      (d) => d.latitude && d.longitude
    );
    if (!points.length) return null;

    const lat =
      points.reduce((sum, d) => sum + parseFloat(d.latitude), 0) / points.length;
    const lon =
      points.reduce((sum, d) => sum + parseFloat(d.longitude), 0) / points.length;
    return { lat, lon };
  };

  setAddressSearchState = (seatId, field, patch) => {
    const key = this.addressSearchKey(seatId, field);
    this.setState((prev) => ({
      addressSearch: {
        ...prev.addressSearch,
        [key]: { ...prev.addressSearch[key], ...patch },
      },
    }));
  };

  handleAddressInputChange = (seatId, field, value) => {
    this.setAddressSearchState(seatId, field, { query: value, open: true, activeIndex: -1 });

    const key = this.addressSearchKey(seatId, field);
    clearTimeout(this.addressSearchTimeouts[key]);

    if (value.trim().length < ADDRESS_SEARCH_MIN_CHARS) {
      this.setAddressSearchState(seatId, field, { results: [], loading: false });
      return;
    }

    this.addressSearchTimeouts[key] = setTimeout(
      () => this.searchAddressForCanvasser(seatId, field, value),
      ADDRESS_SEARCH_DEBOUNCE_MS
    );
  };

  // addresses already assigned (for this same field) to *other* seats in this crew
  // -- lets someone quickly reuse an address (e.g. a shared muster point/building)
  // without typing or re-searching it, surfaced even before the user types anything
  getSharedAddressOptions = (seatId, field, query) => {
    const q = (query || "").trim().toLowerCase();
    const byAddress = {};
    const addressField = `${field}_address`;
    const latField = `${field}_lat`;
    const lonField = `${field}_lon`;

    this.state.canvassers.forEach((c) => {
      if (c.seat_id === seatId) return;
      if (!c[addressField] || c[latField] == null || c[lonField] == null) return;

      const dedupeKey = c[addressField].trim().toLowerCase();
      if (!dedupeKey || (q && !dedupeKey.includes(q))) return;

      if (!byAddress[dedupeKey]) {
        byAddress[dedupeKey] = {
          key: `shared-${dedupeKey}`,
          primary: c[addressField],
          fullAddress: c[addressField],
          lat: c[latField],
          lon: c[lonField],
          names: [],
        };
      }
      byAddress[dedupeKey].names.push(`${c.firstname} ${c.lastname}`.trim());
    });

    return Object.values(byAddress).map(({ names, ...opt }) => ({
      ...opt,
      secondary: `Used by ${names.join(", ")}`,
    }));
  };

  // combined, flat option list backing both the listbox render and keyboard nav --
  // shared (already-used) addresses first, then live autocomplete results
  getAddressOptions = (seatId, field) => {
    const search = this.state.addressSearch[this.addressSearchKey(seatId, field)] || {};
    return [
      ...this.getSharedAddressOptions(seatId, field, search.query),
      ...(search.results || []),
    ];
  };

  // ARIA combobox (list autocomplete) keyboard behavior -- see
  // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/
  handleAddressInputKeyDown = (seatId, field, event) => {
    const search = this.state.addressSearch[this.addressSearchKey(seatId, field)] || {};
    const options = this.getAddressOptions(seatId, field);
    const activeIndex = search.activeIndex ?? -1;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (!options.length) return;
        if (!search.open) {
          this.setAddressSearchState(seatId, field, { open: true, activeIndex: 0 });
          return;
        }
        const next = activeIndex >= options.length - 1 ? 0 : activeIndex + 1;
        this.setAddressSearchState(seatId, field, { activeIndex: next });
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (!options.length) return;
        if (!search.open) {
          this.setAddressSearchState(seatId, field, { open: true, activeIndex: options.length - 1 });
          return;
        }
        const prev = activeIndex <= 0 ? options.length - 1 : activeIndex - 1;
        this.setAddressSearchState(seatId, field, { activeIndex: prev });
        return;
      }
      case "Home": {
        if (!search.open || !options.length) return;
        event.preventDefault();
        this.setAddressSearchState(seatId, field, { activeIndex: 0 });
        return;
      }
      case "End": {
        if (!search.open || !options.length) return;
        event.preventDefault();
        this.setAddressSearchState(seatId, field, { activeIndex: options.length - 1 });
        return;
      }
      case "Enter": {
        if (search.open && activeIndex >= 0 && options[activeIndex]) {
          event.preventDefault();
          this.selectAddressResult(seatId, field, options[activeIndex]);
        }
        return;
      }
      case "Escape": {
        if (search.open) {
          event.preventDefault();
          this.setAddressSearchState(seatId, field, { open: false, activeIndex: -1 });
        }
        return;
      }
      default:
        return;
    }
  };

  // `onResults`, if given, is called with the resolved list (empty on error) --
  // used by primeCanvasserAddressFromClick to auto-assign the top hit without
  // waiting on/duplicating the debounced typing path below
  searchAddressForCanvasser = (seatId, field, query, onResults) => {
    const canvasser = this.state.canvassers.find(
      (d) => d.seat_id === seatId
    );
    if (!canvasser) return;

    const center = this.getCanvasserSearchCenter(canvasser);

    this.setAddressSearchState(seatId, field, { loading: true, error: false });

    this.props
      .searchAddresses(query, { center })
      .then((results) => {
        this.setAddressSearchState(seatId, field, {
          loading: false,
          results: results || [],
        });
        onResults && onResults(results || []);
      })
      .catch(() => {
        this.setAddressSearchState(seatId, field, {
          loading: false,
          error: true,
          results: [],
        });
        onResults && onResults([]);
      });
  };

  // optional per-provider hook (see TurfDropoffMapModalUber's `refineAddress`) that
  // takes a resolved { lat, lon, fullAddress } and returns a Promise of the same
  // shape, possibly narrowed to something more specific -- a no-op when the prop
  // isn't supplied (legacy/Google-sourced addresses have no such concept) or if it
  // rejects, so a provider issue here never blocks the underlying assignment
  resolveFinalAddress = (lat, lon, address) => {
    const refine = this.props.refineAddress;
    if (!refine) return Promise.resolve({ lat, lon, address });

    return refine({ lat, lon, fullAddress: address })
      .then((refined) => ({ lat: refined.lat, lon: refined.lon, address: refined.fullAddress }))
      .catch(() => ({ lat, lon, address }));
  };

  // expects a normalized result: { key, primary, secondary, fullAddress, lat, lon }
  selectAddressResult = (seatId, field, result) => {
    const address = result.fullAddress || result.primary || result.secondary || "";
    this.setAddressSearchState(seatId, field, {
      query: address,
      results: [],
      open: false,
      loading: false,
      activeIndex: -1,
    });

    this.resolveFinalAddress(result.lat, result.lon, address).then(
      ({ lat, lon, address: finalAddress }) => {
        this.assignLocation(seatId, field, lat, lon, finalAddress);
      }
    );
  };

  // map-click counterpart to selectAddressResult: reverse-geocode the clicked point
  // for address *text* (Uber's address API has no lat/lon-to-address lookup, so
  // Google's reverse geocoder is the only source for this), then resolve that text
  // through the same suggestion source the dropdown itself uses and assign the top
  // hit's coordinates -- so the marker lands exactly where manually picking that
  // top suggestion would put it, instead of at Google's (possibly differently
  // sourced) reverse-geocoded point. Falls back to the reverse-geocoded coordinates
  // if the suggestion lookup comes back empty. If the user goes on to pick a
  // different suggestion, selectAddressResult fires its own follow-up
  // assignLocation and simply wins.
  primeCanvasserAddressFromClick = (seatId, field, latitude, longitude) => {
    this.setAddressSearchState(seatId, field, { loading: true, error: false });

    reverseGeocode(latitude, longitude)
      .then((result) => {
        if (!result) {
          this.setAddressSearchState(seatId, field, { loading: false });
          return;
        }

        const address = result.primary || result.secondary || "";
        this.setAddressSearchState(seatId, field, { query: address, open: true });

        this.searchAddressForCanvasser(seatId, field, address, (results) => {
          const top = results[0];
          const [lat, lon] = top ? [top.lat, top.lon] : [result.lat, result.lon];
          this.resolveFinalAddress(lat, lon, address).then(
            ({ lat: finalLat, lon: finalLon, address: finalAddress }) => {
              this.assignLocation(seatId, field, finalLat, finalLon, finalAddress);
            }
          );
        });
      })
      .catch(() => {
        this.setAddressSearchState(seatId, field, { loading: false, error: true });
      });
  };

  handleClickOutsideAddressSearch = (event) => {
    Object.entries(this.addressSearchRefs).forEach(([key, ref]) => {
      if (ref && !ref.contains(event.target)) {
        this.setState((prev) => {
          const entry = prev.addressSearch[key];
          if (!entry || !entry.open) return null;
          return {
            addressSearch: {
              ...prev.addressSearch,
              [key]: { ...entry, open: false, activeIndex: -1 },
            },
          };
        });
      }
    });
  };

  // `field` is "dropoff" | "ending_pickup"; `label` is the placeholder/aria-label text
  renderAddressSearch = (canvasser, field, label) => {
    const seatId = canvasser.seat_id;
    const searchKey = this.addressSearchKey(seatId, field);
    const search = this.state.addressSearch[searchKey] || {};
    const activeIndex = search.activeIndex ?? -1;
    const sharedOptions = this.getSharedAddressOptions(seatId, field, search.query);
    const apiResults = search.results || [];
    const options = [...sharedOptions, ...apiResults];
    const listboxId = `address-listbox-${searchKey}`;
    const activeOptionId =
      activeIndex >= 0 && options[activeIndex] ? `address-option-${searchKey}-${activeIndex}` : undefined;
    const showDropdown = !!(search.open && options.length);
    const showError = !!(search.open && search.error && !options.length);
    const isActiveClickTarget =
      this.state.selectedSeatId === seatId && this.state.selectedField === field;

    return (
      <div
        className={`canvasser-card__address-search${isActiveClickTarget ? " active-target" : ""}`}
        ref={(el) => (this.addressSearchRefs[searchKey] = el)}
        onClick={(e) => {
          e.stopPropagation();
          this.focusCanvasser(seatId, field);
        }}
      >
        <input
          type={"text"}
          role={"combobox"}
          aria-autocomplete={"list"}
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-label={`Search ${label.toLowerCase()}`}
          className={"canvasser-card__address-input"}
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search.query || ""}
          onChange={(e) => this.handleAddressInputChange(seatId, field, e.target.value)}
          onFocus={() => {
            this.focusCanvasser(seatId, field);
            this.setAddressSearchState(seatId, field, { open: true });
          }}
          onKeyDown={(e) => this.handleAddressInputKeyDown(seatId, field, e)}
        />
        {search.loading && (
          <i
            className={
              "fa fa-spinner fa-spin canvasser-card__address-spinner"
            }
          />
        )}
        {showDropdown && (
          <ul className={"canvasser-card__address-dropdown"} id={listboxId} role={"listbox"}>
            {sharedOptions.length > 0 && (
              <li className={"canvasser-card__address-group-label"} role={"presentation"}>
                Used by other seats
              </li>
            )}
            {options.map((r, index) => (
              <li
                key={r.key}
                id={`address-option-${searchKey}-${index}`}
                role={"option"}
                aria-selected={index === activeIndex}
                className={`canvasser-card__address-option${
                  index === activeIndex ? " active" : ""
                }`}
                onMouseEnter={() => this.setAddressSearchState(seatId, field, { activeIndex: index })}
                onClick={() => this.selectAddressResult(seatId, field, r)}
              >
                {r.primary && (
                  <div className={"address-option__name"}>{r.primary}</div>
                )}
                {r.secondary && (
                  <div className={"address-option__address"}>
                    {r.secondary}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {showError && (
          <ul className={"canvasser-card__address-dropdown"} id={listboxId} role={"listbox"}>
            <li className={"canvasser-card__address-option error-text"} role={"presentation"}>
              Couldn&apos;t search addresses. Try again.
            </li>
          </ul>
        )}
      </div>
    );
  };

  // one field's worth of a canvasser card: label + combobox + assigned/unassign row.
  // shared between the dropoff and ending-pickup sections of renderCanvasserCards
  renderAddressField = (canvasser, field, label) => {
    const assigned = !!canvasser[`${field}_lat`];
    return (
      <React.Fragment>
        <label className={"canvasser-card__field-label"}>{label}</label>
        {this.renderAddressSearch(canvasser, field, label)}

        <div className={"canvasser-card__status-row"}>
          {assigned ? (
            <>
              <span className={"success-text"}>
                <i className={"fa fa-check-circle"} /> Assigned
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  this.unassignLocation(canvasser.seat_id, field);
                }}
                className={"canvasser-card__unassign-btn"}
                title={`Unassign ${label.toLowerCase()}`}
              >
                <i className={"fa fa-times-circle"} />
              </button>
            </>
          ) : (
            <span className={"canvasser-card__status-placeholder"}>
              Click the map or search an address
            </span>
          )}
        </div>
      </React.Fragment>
    );
  };

  renderCanvasserCards = () => {
    // sorted by name (not selection/assignment state) and off of a copy -- so card
    // order stays fixed as canvassers get selected/assigned instead of shuffling
    return [...this.state.canvassers]
      .sort((a, b) =>
        `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`)
      )
      .map((d) => {
        let color = this.getColorFromQualitativeScheme(d.seat_id);
        let selected = d.seat_id === this.state.selectedSeatId;
        let dropoffAssigned = !!d.dropoff_lat;
        return (
          <div
            className={`canvasser-card ${selected ? "selected" : ""}`}
            key={d.seat_id}
            style={{
              borderLeftColor: color,
            }}
            onClick={() => this.focusCanvasser(d.seat_id)}
            onMouseEnter={() => this.handleCanvasserCardMouseOver(d)}
            onMouseLeave={() => this.handleCanvasserCardMouseOut()}
          >
            <div className={"canvasser-card__header"}>
              <span className={"canvasser-name"}>
                {d.firstname} {d.lastname}
              </span>
              <span
                className={`canvasser-card__status-dot ${
                  dropoffAssigned ? "assigned" : ""
                }`}
                title={dropoffAssigned ? "Dropoff assigned" : "Dropoff not yet assigned"}
              />
            </div>

            {this.renderAddressField(d, "dropoff", "Dropoff Address")}
            {this.renderAddressField(d, "ending_pickup", "Ending Pickup Address")}
          </div>
        );
      });
  };

  render() {
    return (
      <div className={"map-modal"}>
        <FeedbackTile
          text={
            <div>
              <i
                className=" fas fa-asterisk fa-spin"
                style={{ marginRight: "15px" }}
              />
              Loading...
            </div>
          }
          visible={this.state.loading}
        />

        <div className={"map-modal__map"}>
          <OLLayerMap
            layers={this.state.layers}
            onClick={this.handleMapClick}
            onMarkerHover={this.handleFeatureHover}
          />
          <div className={"map-modal__sidebar"}>
            <div className={"map-modal__sidebar__title"}>
              <h3 className={"primary-text"}>Dropoff Points</h3>
              <h5 className={"primary-text"}>Crew {this.props.crew}</h5>
            </div>

            {this.renderCanvasserCards()}

            {!!this.state.canvassers.length &&
              this.state.canvassers.every((d) => d.dropoff_lat) && (
                <div className={"map-modal__sidebar__success-msg"}>
                  <i className={"fa fa-check-circle"} /> All Assigned
                </div>
              )}

            <div className={"map-modal__sidebar__close-button"}>
              <div
                className={"button button-outline"}
                onClick={this.props.onClose}
              >
                I&apos;m done
              </div>
            </div>
          </div>
        </div>
        <div
          className={"map-modal__close-button"}
          onClick={this.props.onClose}
        ></div>
      </div>
    );
  }
}

export default TurfDropoffMapModalBase;
